import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import * as fmt from '../../lib/format';

interface MapRow {
  project_key: string;
  logical_name: string;
  jira_field_id: string | null;
  notes: string | null;
  updated_at: string;
}

interface JiraField {
  id: string;
  name: string;
  schema: string | null;
}

export default function JiraFieldMappingTab() {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [rows, setRows] = useState<MapRow[]>([]);
  const [fields, setFields] = useState<JiraField[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const loadMap = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiService.getCustomFieldMap();
      if (r.success) setRows(r.data ?? []);
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (user?.role === 'admin') void loadMap();
  }, [user, loadMap]);

  const detectFields = async () => {
    setDetecting(true);
    try {
      const r = await apiService.getJiraCustomFields();
      if (r.success) {
        setFields((r.data ?? []).slice().sort((a: JiraField, b: JiraField) => a.name.localeCompare(b.name)));
        showToast('success', `Loaded ${r.data?.length ?? 0} custom fields from Jira`);
      } else {
        showToast('error', r.error || 'Failed to detect fields');
      }
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Failed to detect fields');
    } finally {
      setDetecting(false);
    }
  };

  const save = async (logicalName: string, newFieldId: string) => {
    setSavingKey(logicalName);
    try {
      const r = await apiService.updateCustomFieldMap(logicalName, {
        jira_field_id: newFieldId || null,
      });
      if (r.success) {
        showToast('success', `Saved mapping for ${logicalName}`);
        await loadMap();
      } else {
        showToast('error', r.error || 'Failed to save');
      }
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Jira field mapping</h2>
          <p className="text-sm text-gray-600 mt-1">
            Map each logical planning field to its Jira <code>customfield_*</code> ID. NULL entries mean the field
            isn&apos;t used yet — issues won&apos;t receive a value for that logical name until it&apos;s mapped.
          </p>
        </div>
        <button
          onClick={detectFields}
          disabled={detecting}
          className="px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {detecting ? 'Detecting…' : 'Detect fields from Jira'}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] text-sm">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="py-2 pr-4 font-medium text-gray-700">Logical name</th>
              <th className="py-2 pr-4 font-medium text-gray-700">Project</th>
              <th className="py-2 pr-4 font-medium text-gray-700">Jira field ID</th>
              <th className="py-2 pr-4 font-medium text-gray-700">Notes</th>
              <th className="py-2 font-medium text-gray-700"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <MappingRow
                key={`${row.project_key}:${row.logical_name}`}
                row={row}
                fields={fields}
                saving={savingKey === row.logical_name}
                onSave={(v) => save(row.logical_name, v)}
              />
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function MappingRow({
  row,
  fields,
  saving,
  onSave,
}: {
  row: MapRow;
  fields: JiraField[];
  saving: boolean;
  onSave: (jiraFieldId: string) => void;
}) {
  const [value, setValue] = useState<string>(row.jira_field_id ?? '');
  useEffect(() => setValue(row.jira_field_id ?? ''), [row.jira_field_id]);
  const dirty = value !== (row.jira_field_id ?? '');

  return (
    <tr className="border-b border-gray-100">
      <td className="py-2 pr-4 font-mono text-xs text-gray-800">{row.logical_name}</td>
      <td className="py-2 pr-4 text-gray-600">{row.project_key}</td>
      <td className="py-2 pr-4">
        {fields.length > 0 ? (
          <select
            aria-label={`Jira field for ${row.logical_name} in ${row.project_key}`}
            className="w-56 border border-gray-300 rounded px-2 py-1 text-sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          >
            <option value="">— unmapped —</option>
            {fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} ({f.id})
              </option>
            ))}
          </select>
        ) : (
          <input
            aria-label={`Jira field ID for ${row.logical_name} in ${row.project_key}`}
            className="w-56 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
            placeholder="customfield_10000 or empty"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </td>
      <td className="py-2 pr-4 text-gray-500 text-xs">{row.notes ?? fmt.MISSING}</td>
      <td className="py-2">
        <button
          disabled={!dirty || saving}
          onClick={() => onSave(value)}
          className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </td>
    </tr>
  );
}
